import { clsx } from 'clsx'
import { type FC, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconArrowRight, IconSavings } from '@redteclab/icons'

import { Button } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { urlResolverGetLinkToAllProductsOfSeller } from '../../../url-handling'
import { productAccessorGetSeller } from '../../model/productAccessor/productAccessorGetSeller'
import { productAccessorGetSellerPromotionFromOffer } from '../../model/productAccessor/productAccessorGetSellerPromotionFromOffer'
import { productAccessorOfferHasSellerPromotion } from '../../model/productAccessor/productAccessorOfferHasSellerPromotion'
import { productAccessorStockStatusIsAvailable } from '../../model/productAccessor/productAccessorStockStatusIsAvailable'

import { type ProductSellerPromotionInfoBannerProps } from './ProductSellerPromotionInfoBannerTypes'
import { renderProductSellerPromotionInfoBannerMessage } from './renderProductSellerPromotionInfoBannerMessage'

export const ProductSellerPromotionInfoBannerHarmonisation: FC<
  ProductSellerPromotionInfoBannerProps & { title: ReactNode }
> = ({ className, offer, product, title }) => {
  const config = useGlobalConfigContext()
  const intl = useIntl()

  if (!offer?.seller?.sellerId) {
    return null
  }

  const productOfferHasSellerPromotion =
    productAccessorOfferHasSellerPromotion(offer)

  const seller = productAccessorGetSeller(offer)
  const sellerPromotion = productAccessorGetSellerPromotionFromOffer(offer)

  const showSellerSerpLink =
    sellerPromotion?.conditionType === 'minimumOrderValue' ||
    sellerPromotion?.conditionType === 'minimumQuantity'

  return (
    productAccessorStockStatusIsAvailable(product) &&
    productOfferHasSellerPromotion &&
    sellerPromotion && (
      <div
        className={clsx(
          'flex rounded-2xl bg-light-primary-low p-4 tablet:w-[640px]',
          className,
        )}
        data-qa-id="product-seller-promotion-info-banner"
      >
        <div
          className="flex size-12 shrink-0 items-center justify-center rounded-full bg-light-tertiary font-mono text-2xl font-medium"
          data-qa-id="product-seller-promotion-banner-badge"
        >
          <IconSavings />
        </div>
        <div
          className={clsx(
            'ml-4 flex flex-col items-start gap-1 text-xs tablet:text-s',
            showSellerSerpLink ? 'justify-between' : 'justify-center',
          )}
        >
          <p className="text-s font-medium">{title}</p>
          <p data-qa-id="product-seller-promotion-banner-description">
            {renderProductSellerPromotionInfoBannerMessage({
              footnoteNumber: config.superScripts.sellerPromotions,
              intl,
              sellerName: seller?.displayName,
              sellerPromotion,
              tenant: config.tenant,
            })}
          </p>
          {showSellerSerpLink ? (
            <Button<'a'>
              className="tablet:font-medium"
              data-qa-id="product-seller-promotion-banner-seller-serp-link"
              endIcon={<IconArrowRight />}
              href={urlResolverGetLinkToAllProductsOfSeller(
                config,
                offer.seller.sellerId,
              )}
              size="medium"
              slots={{ root: 'a' }}
              variant="text"
            >
              <FormattedMessage id="seller.promotion.product.banner.sellerSerpLink" />
            </Button>
          ) : null}
        </div>
      </div>
    )
  )
}
